import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  Compass,
  CreditCard,
  Globe2,
  HeartPulse,
  Map as MapIcon,
  PiggyBank,
  Repeat,
  Sparkles,
  Swords,
  TrendingUp,
  UserPlus,
  Users,
  XCircle,
  Zap,
} from "lucide-react";
import toast from "react-hot-toast";
import { supabase } from "@/lib/supabase";
import { formatMoneyMinor } from "@/lib/money";
import { PageLoader } from "@/components/brand/PageLoader";

/**
 * Admin → Analytics. The KPI dashboard the founding team monitors
 * daily. Sections mirror the marketplace-startup playbook: acquisition,
 * activation, games (the actual product), payments, venues, retention.
 *
 * Where data isn't available yet (signup source, NPS, ad-spend CAC),
 * we render explicit placeholder cards so it's obvious WHY a number
 * isn't there - not pretend it's zero.
 */

interface ProfileRow {
  id: string;
  created_at: string;
}
interface ApplicationRow {
  status: string;
  city: string | null;
  country: string;
  venue_name: string;
  created_at: string;
  reviewed_at: string | null;
  invited_at: string | null;
}
interface MatchRow {
  id: string;
  status: string;
  format: string;
  target_players: number;
  unit_price_minor: number;
  platform_fee_minor: number;
  currency: string;
  created_at: string;
  confirmed_at: string | null;
  court_id: string;
  host_id: string;
  // Embedded court → venue
  court?: {
    venue_application_id: string;
    venue_application?: { city: string | null; venue_name: string } | null;
  } | null;
}
interface ParticipantRow {
  match_id: string;
  user_id: string;
  status: string;
  unit_price_minor: number;
  platform_fee_minor: number;
  created_at: string;
}
interface FlagRow {
  id: string;
  created_at: string;
}

const COUNTRY_NAME: Record<string, string> = {
  GB: "United Kingdom",
  US: "United States",
  CA: "Canada",
  AU: "Australia",
  NZ: "New Zealand",
};

export function AdminAnalytics() {
  const [profiles, setProfiles] = useState<ProfileRow[] | null>(null);
  const [apps, setApps] = useState<ApplicationRow[] | null>(null);
  const [matches, setMatches] = useState<MatchRow[] | null>(null);
  const [participants, setParticipants] = useState<ParticipantRow[] | null>(
    null,
  );
  const [flags, setFlags] = useState<FlagRow[] | null>(null);

  useEffect(() => {
    Promise.all([
      supabase.from("profiles").select("id, created_at"),
      supabase
        .from("venue_applications")
        .select(
          "status, city, country, venue_name, created_at, reviewed_at, invited_at",
        ),
      supabase
        .from("matches")
        .select(
          `id, status, format, target_players, unit_price_minor,
           platform_fee_minor, currency, created_at, confirmed_at,
           court_id, host_id,
           court:court_id (
             venue_application_id,
             venue_application:venue_application_id ( city, venue_name )
           )`,
        ),
      supabase
        .from("match_participants")
        .select(
          "match_id, user_id, status, unit_price_minor, platform_fee_minor, created_at",
        ),
      supabase.from("no_show_flags").select("id, created_at"),
    ]).then(
      ([profilesRes, appsRes, matchesRes, participantsRes, flagsRes]) => {
        const firstErr =
          profilesRes.error ||
          appsRes.error ||
          matchesRes.error ||
          participantsRes.error ||
          flagsRes.error;
        if (firstErr) {
          console.error("[AdminAnalytics] load error", firstErr);
          toast.error(`Couldn't load analytics: ${firstErr.message}`);
        }
        setProfiles((profilesRes.data ?? []) as ProfileRow[]);
        setApps((appsRes.data ?? []) as ApplicationRow[]);
        setMatches((matchesRes.data ?? []) as unknown as MatchRow[]);
        setParticipants(
          (participantsRes.data ?? []) as ParticipantRow[],
        );
        setFlags((flagsRes.data ?? []) as FlagRow[]);
      },
    );
  }, []);

  const k = useMemo(() => {
    if (!profiles || !apps || !matches || !participants || !flags) return null;
    return computeMetrics(profiles, apps, matches, participants, flags);
  }, [profiles, apps, matches, participants, flags]);

  return (
    <div>
      <header className="mb-8">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Admin · Analytics
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">Analytics</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          The KPIs that matter from day one. Anything marked with a
          dashed border is waiting on a data source we don't have yet
          (UTM tracking, app-event SDK, NPS survey infra, ad spend) -
          shown so it's obvious what's missing, not because the number
          is zero.
        </p>
      </header>

      {!k ? (
        <PageLoader label="Crunching…" />
      ) : (
        <>
          {/* USER ACQUISITION */}
          <Section
            heading="User acquisition"
            icon={<UserPlus className="h-4 w-4" />}
          >
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat
                icon={<UserPlus className="h-4 w-4" />}
                label="New signups · today"
                value={k.signupsToday}
              />
              <Stat
                icon={<UserPlus className="h-4 w-4" />}
                label="Last 7 days"
                value={k.signups7d}
                tone="volt"
                sub={diffWoW(k.signups7d, k.signups7dPrior)}
              />
              <Stat
                icon={<UserPlus className="h-4 w-4" />}
                label="Last 30 days"
                value={k.signups30d}
                sub={diffWoW(k.signups30d, k.signups30dPrior, "vs prior 30d")}
              />
              <Stat
                icon={<TrendingUp className="h-4 w-4" />}
                label="All-time"
                value={k.signupsTotal}
              />
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <ComingSoon
                label="Signup source"
                why="Add UTM tags to sign-up links (?utm_source=…) so we can split organic / referral / paid / social."
              />
              <ComingSoon
                label="Conversion · app open → signup"
                why="Activates when the mobile app starts emitting screen-view events through PostHog."
              />
              <ComingSoon
                label="Waitlist conversion"
                why="No waitlist active - we let venue applications and player signups land directly."
              />
            </div>
          </Section>

          {/* ACTIVATION */}
          <Section
            heading="Activation · the most important early metric"
            icon={<Zap className="h-4 w-4" />}
            badge="North star"
          >
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Stat
                icon={<CheckCircle2 className="h-4 w-4" />}
                label="Activated within 7 days"
                value={
                  k.activationPct === null ? "-" : `${k.activationPct}%`
                }
                tone="volt"
                sub={
                  k.activationPct === null
                    ? "Need ≥ 1 signup that's 7+ days old"
                    : `${k.activatedCount} of ${k.eligibleForActivation} eligible signups`
                }
              />
              <Stat
                icon={<Clock className="h-4 w-4" />}
                label="Time to first game"
                value={
                  k.medianHoursToFirst === null
                    ? "-"
                    : formatHours(k.medianHoursToFirst)
                }
                sub="Median from signup to first match join"
              />
              <Stat
                icon={<XCircle className="h-4 w-4" />}
                label="Never joined a game"
                value={
                  k.noActivationPct === null
                    ? "-"
                    : `${k.noActivationPct}%`
                }
                sub={
                  k.noActivationPct === null
                    ? undefined
                    : "Of signups that are 30+ days old"
                }
              />
            </div>
          </Section>

          {/* GAMES */}
          <Section heading="Games" icon={<Swords className="h-4 w-4" />}>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat
                icon={<Swords className="h-4 w-4" />}
                label="Games created · 7d"
                value={k.gamesCreated7d}
                sub={`${k.gamesCreatedAvgPerDay7d}/day average`}
              />
              <Stat
                icon={<CheckCircle2 className="h-4 w-4" />}
                label="Confirmed vs cancelled"
                value={
                  k.confirmedVsCancelledRatio === null
                    ? "-"
                    : k.confirmedVsCancelledRatio
                }
                tone={
                  k.confirmedVsCancelledRatioNumeric !== null &&
                  k.confirmedVsCancelledRatioNumeric < 1
                    ? "danger"
                    : "volt"
                }
                sub={
                  k.confirmedVsCancelledRatio === null
                    ? "No data yet"
                    : k.confirmedVsCancelledRatioNumeric !== null &&
                        k.confirmedVsCancelledRatioNumeric < 1
                      ? "Supply problem - more cancels than confirms"
                      : `${k.matchConfirmed} confirmed · ${k.matchCancelled} cancelled`
                }
              />
              <Stat
                icon={<Users className="h-4 w-4" />}
                label="Avg players / game"
                value={
                  k.avgPlayersPerGame === null
                    ? "-"
                    : k.avgPlayersPerGame.toFixed(1)
                }
                sub="Captured seats per match"
              />
              <Stat
                icon={<Sparkles className="h-4 w-4" />}
                label="Most popular format"
                value={k.topFormat ?? "-"}
                sub={
                  k.topFormatPct === null
                    ? undefined
                    : `${k.topFormatPct}% of all games`
                }
              />
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <Card title="Format mix" icon={<Swords className="h-4 w-4" />}>
                {k.formatMix.length === 0 ? (
                  <EmptyNote>No matches yet.</EmptyNote>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {k.formatMix.map(([format, count]) => (
                      <BreakdownRow
                        key={format}
                        label={format}
                        count={count}
                        total={k.matchTotal}
                      />
                    ))}
                  </ul>
                )}
              </Card>
              <Card
                title="Games by city"
                icon={<MapIcon className="h-4 w-4" />}
              >
                {k.cityMix.length === 0 ? (
                  <EmptyNote>
                    Cities show up once matches are tied to venue cities.
                  </EmptyNote>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {k.cityMix.slice(0, 6).map(([city, count]) => (
                      <BreakdownRow
                        key={city}
                        label={city}
                        count={count}
                        total={k.matchTotal}
                      />
                    ))}
                  </ul>
                )}
              </Card>
            </div>
            <div className="mt-3">
              <Card
                title="Games created · last 14 days"
                icon={<BarChart3 className="h-4 w-4" />}
              >
                <DailyBars days={k.dailyMatchCounts} />
              </Card>
            </div>
          </Section>

          {/* PAYMENTS */}
          <Section
            heading="Payments"
            icon={<CreditCard className="h-4 w-4" />}
          >
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat
                icon={<PiggyBank className="h-4 w-4" />}
                label="GMV (all-time)"
                value={formatMoneyMinor(k.gmvMinor, k.currency)}
                sub="Total money flowing through the platform"
              />
              <Stat
                icon={<TrendingUp className="h-4 w-4" />}
                label="Net revenue"
                value={formatMoneyMinor(k.netRevenueMinor, k.currency)}
                tone="volt"
                sub="Whistll's 20% platform fee, captured"
              />
              <Stat
                icon={<Sparkles className="h-4 w-4" />}
                label="Avg revenue / confirmed game"
                value={
                  k.avgRevenuePerGameMinor === null
                    ? "-"
                    : formatMoneyMinor(
                        k.avgRevenuePerGameMinor,
                        k.currency,
                      )
                }
                sub={
                  k.avgRevenuePerGameMinor === null
                    ? "No confirmed games yet"
                    : `Across ${k.confirmedGameCount} games`
                }
              />
              <Stat
                icon={<AlertTriangle className="h-4 w-4" />}
                label="Chargebacks"
                value="-"
                sub="See Admin → Payments for live Stripe data"
                muted
              />
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <ComingSoon
                label="Failed pre-authorisations"
                why="Subscribe to Stripe payment_intent.payment_failed in stripe-webhook + persist a counter so we can show authorisation friction over time."
              />
              <ComingSoon
                label="Chargeback rate"
                why="The Admin → Payments tab pulls live Stripe disputes today. Roll into here once we want a 7d/30d trend."
              />
            </div>
          </Section>

          {/* VENUES */}
          <Section heading="Venues" icon={<Building2 className="h-4 w-4" />}>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat
                icon={<Building2 className="h-4 w-4" />}
                label="Applied"
                value={k.venuesTotal}
              />
              <Stat
                icon={<CheckCircle2 className="h-4 w-4" />}
                label="Approved"
                value={k.venuesApproved}
                sub={
                  k.venuesTotal > 0
                    ? `${Math.round((k.venuesApproved / k.venuesTotal) * 100)}% conversion`
                    : undefined
                }
              />
              <Stat
                icon={<Activity className="h-4 w-4" />}
                label="Live (≥ 1 match)"
                value={k.venuesLive}
                tone="volt"
                sub={
                  k.venuesApproved > 0
                    ? `${Math.round((k.venuesLive / k.venuesApproved) * 100)}% of approved`
                    : undefined
                }
              />
              <Stat
                icon={<TrendingUp className="h-4 w-4" />}
                label="Avg bookings / venue / week"
                value={
                  k.avgBookingsPerVenuePerWeek === null
                    ? "-"
                    : k.avgBookingsPerVenuePerWeek.toFixed(1)
                }
                sub="Across live venues, last 4 weeks"
              />
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <Card
                title="Utilisation by day of week"
                icon={<Calendar className="h-4 w-4" />}
              >
                {k.matchTotal === 0 ? (
                  <EmptyNote>Need confirmed matches first.</EmptyNote>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {k.bookingsByDow.map(([dow, count]) => (
                      <BreakdownRow
                        key={dow}
                        label={dow}
                        count={count}
                        total={k.matchTotal}
                      />
                    ))}
                  </ul>
                )}
              </Card>
              <Card
                title="Approved venues with zero bookings"
                icon={<AlertTriangle className="h-4 w-4" />}
              >
                {k.zeroBookingVenues.length === 0 ? (
                  <EmptyNote>
                    Every approved venue has hosted at least one match.
                  </EmptyNote>
                ) : (
                  <ul className="flex flex-col gap-2 text-sm">
                    {k.zeroBookingVenues.slice(0, 8).map((v) => (
                      <li
                        key={v}
                        className="flex items-center justify-between rounded-md border border-dashed border-border/60 px-3 py-2"
                      >
                        <span className="font-semibold">{v}</span>
                        <span className="text-xs text-muted-foreground">
                          Outreach or remove
                        </span>
                      </li>
                    ))}
                    {k.zeroBookingVenues.length > 8 && (
                      <li className="px-1 text-xs text-muted-foreground">
                        +{k.zeroBookingVenues.length - 8} more
                      </li>
                    )}
                  </ul>
                )}
              </Card>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <Stat
                icon={<Compass className="h-4 w-4" />}
                label="Avg time to review"
                value={
                  k.avgReviewHours === null ? "-" : `${k.avgReviewHours}h`
                }
                sub="Target: < 48h"
              />
              <Stat
                icon={<CheckCircle2 className="h-4 w-4" />}
                label="Invites sent"
                value={k.invitesSent}
                sub="Approved + magic-link delivered"
              />
              <Stat
                icon={<Globe2 className="h-4 w-4" />}
                label="Top country"
                value={
                  k.topCountry === null
                    ? "-"
                    : (COUNTRY_NAME[k.topCountry] ?? k.topCountry)
                }
                sub={
                  k.topCountryCount === null
                    ? undefined
                    : `${k.topCountryCount} applications`
                }
              />
            </div>
          </Section>

          {/* RETENTION */}
          <Section
            heading="Retention"
            icon={<Repeat className="h-4 w-4" />}
          >
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat
                icon={<HeartPulse className="h-4 w-4" />}
                label="7-day retention"
                value={
                  k.retention7d === null ? "-" : `${k.retention7d}%`
                }
                tone="volt"
                sub={
                  k.retention7d === null
                    ? "Need signups 14+ days old"
                    : "Of users 14d post-signup, joined a game in the 2nd week"
                }
              />
              <Stat
                icon={<HeartPulse className="h-4 w-4" />}
                label="30-day retention"
                value={
                  k.retention30d === null ? "-" : `${k.retention30d}%`
                }
                sub={
                  k.retention30d === null
                    ? "Need signups 60+ days old"
                    : "Of users 60d post-signup, joined a game in days 30-60"
                }
              />
              <Stat
                icon={<Repeat className="h-4 w-4" />}
                label="Games / user / month"
                value={
                  k.gamesPerUserPerMonth === null
                    ? "-"
                    : k.gamesPerUserPerMonth.toFixed(1)
                }
                sub="Across users with ≥ 1 game"
              />
              <Stat
                icon={<Sparkles className="h-4 w-4" />}
                label="Power users (3+ games)"
                value={
                  k.powerUserPct === null ? "-" : `${k.powerUserPct}%`
                }
                sub={
                  k.powerUserPct === null
                    ? undefined
                    : `${k.powerUserCount} of ${k.usersWithGames} active users`
                }
              />
            </div>
          </Section>

          {/* MONTH 2+ */}
          <Section
            heading="Once you have data · month 2+"
            icon={<TrendingUp className="h-4 w-4" />}
          >
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <ComingSoon
                label="Cohort analysis"
                why="Once 4+ weekly cohorts of signups exist, we'll show whether week-1 cohorts retain better than week-4 ones."
              />
              <ComingSoon
                label="LTV per user"
                why={`Lifetime value is computable now (${formatMoneyMinor(k.netRevenueMinor, k.currency)} ÷ ${k.usersWithGames || 0} active users) but the number is too noisy with current sample size.`}
              />
              <ComingSoon
                label="CAC by channel"
                why="Needs ad spend data per channel. Pipe in once you start running paid acquisition."
              />
              <ComingSoon
                label="NPS"
                why="Add an in-app prompt after 3 games asking 'how likely are you to recommend Whistll' and persist responses."
              />
              <ComingSoon
                label="Geographic heat maps"
                why="Need lat/long on courts (we only store city). Add geocoding to venue applications and we can render a real map."
              />
            </div>
          </Section>
        </>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Metric computation
// ----------------------------------------------------------------------------

function computeMetrics(
  profiles: ProfileRow[],
  apps: ApplicationRow[],
  matches: MatchRow[],
  participants: ParticipantRow[],
  flags: FlagRow[],
) {
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;

  // -- Signups ----------------------------------------------------------------
  const signupsTotal = profiles.length;
  const signupsToday = profiles.filter(
    (p) => isSameDay(new Date(p.created_at), new Date()),
  ).length;
  const signups7d = profiles.filter(
    (p) => now - new Date(p.created_at).getTime() < 7 * dayMs,
  ).length;
  const signups7dPrior = profiles.filter((p) => {
    const t = new Date(p.created_at).getTime();
    return now - t >= 7 * dayMs && now - t < 14 * dayMs;
  }).length;
  const signups30d = profiles.filter(
    (p) => now - new Date(p.created_at).getTime() < 30 * dayMs,
  ).length;
  const signups30dPrior = profiles.filter((p) => {
    const t = new Date(p.created_at).getTime();
    return now - t >= 30 * dayMs && now - t < 60 * dayMs;
  }).length;

  // -- Activation -------------------------------------------------------------
  // Build a map: user_id → earliest match_participants.created_at
  const firstActivityByUser = new Map<string, number>();
  for (const p of participants) {
    if (p.status === "left" || p.status === "released") continue;
    const t = new Date(p.created_at).getTime();
    const prev = firstActivityByUser.get(p.user_id);
    if (prev === undefined || t < prev) firstActivityByUser.set(p.user_id, t);
  }

  const eligibleForActivation = profiles.filter(
    (p) => now - new Date(p.created_at).getTime() >= 7 * dayMs,
  );
  let activatedCount = 0;
  let timeDiffsHours: number[] = [];
  for (const profile of eligibleForActivation) {
    const signupT = new Date(profile.created_at).getTime();
    const firstT = firstActivityByUser.get(profile.id);
    if (firstT !== undefined && firstT - signupT <= 7 * dayMs) {
      activatedCount++;
      timeDiffsHours.push((firstT - signupT) / (60 * 60 * 1000));
    }
  }
  const activationPct =
    eligibleForActivation.length > 0
      ? Math.round((activatedCount / eligibleForActivation.length) * 100)
      : null;
  const medianHoursToFirst = median(timeDiffsHours);

  // % who never joined a game (signups 30+ days old, no participants row)
  const eligibleForChurn = profiles.filter(
    (p) => now - new Date(p.created_at).getTime() >= 30 * dayMs,
  );
  const noActivationCount = eligibleForChurn.filter(
    (p) => !firstActivityByUser.has(p.id),
  ).length;
  const noActivationPct =
    eligibleForChurn.length > 0
      ? Math.round((noActivationCount / eligibleForChurn.length) * 100)
      : null;

  // -- Games ------------------------------------------------------------------
  const matchTotal = matches.length;
  const matchConfirmed = matches.filter(
    (m) => m.status === "confirmed" || m.status === "completed",
  ).length;
  const matchCancelled = matches.filter((m) => m.status === "cancelled").length;
  const confirmedVsCancelledRatioNumeric =
    matchCancelled > 0
      ? matchConfirmed / matchCancelled
      : matchConfirmed > 0
        ? null
        : null;
  const confirmedVsCancelledRatio =
    confirmedVsCancelledRatioNumeric === null
      ? matchConfirmed > 0
        ? "All confirmed"
        : null
      : `${confirmedVsCancelledRatioNumeric.toFixed(1)} : 1`;

  const gamesCreated7d = matches.filter(
    (m) => now - new Date(m.created_at).getTime() < 7 * dayMs,
  ).length;
  const gamesCreatedAvgPerDay7d = (gamesCreated7d / 7).toFixed(1);

  // Avg players per game (captured seats / matches that have any captured)
  const matchesWithCaptured = new Set<string>();
  let totalCaptured = 0;
  for (const p of participants) {
    if (p.status === "captured") {
      totalCaptured++;
      matchesWithCaptured.add(p.match_id);
    }
  }
  const avgPlayersPerGame =
    matchesWithCaptured.size > 0
      ? totalCaptured / matchesWithCaptured.size
      : null;

  const formatCounts = new Map<string, number>();
  for (const m of matches) {
    formatCounts.set(m.format, (formatCounts.get(m.format) ?? 0) + 1);
  }
  const formatMix = [...formatCounts.entries()].sort((a, b) => b[1] - a[1]);
  const topFormat = formatMix[0]?.[0] ?? null;
  const topFormatPct =
    formatMix[0] && matchTotal > 0
      ? Math.round((formatMix[0][1] / matchTotal) * 100)
      : null;

  const cityCounts = new Map<string, number>();
  for (const m of matches) {
    const city = m.court?.venue_application?.city ?? null;
    if (!city) continue;
    cityCounts.set(city, (cityCounts.get(city) ?? 0) + 1);
  }
  const cityMix = [...cityCounts.entries()].sort((a, b) => b[1] - a[1]);

  // Daily match counts for last 14d
  const dailyMatchCounts: { label: string; count: number }[] = [];
  for (let i = 13; i >= 0; i--) {
    const dayEnd = new Date();
    dayEnd.setHours(0, 0, 0, 0);
    dayEnd.setDate(dayEnd.getDate() - i + 1);
    const dayStart = new Date(dayEnd);
    dayStart.setDate(dayStart.getDate() - 1);
    const count = matches.filter((m) => {
      const t = new Date(m.created_at);
      return t >= dayStart && t < dayEnd;
    }).length;
    dailyMatchCounts.push({
      label: dayStart.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
      }),
      count,
    });
  }

  // -- Payments ---------------------------------------------------------------
  let gmvMinor = 0;
  let netRevenueMinor = 0;
  let currency = "GBP";
  for (const p of participants) {
    if (p.status !== "captured") continue;
    gmvMinor += p.unit_price_minor + p.platform_fee_minor;
    netRevenueMinor += p.platform_fee_minor;
  }
  if (matches[0]) currency = matches[0].currency;
  const confirmedGameCount = matchesWithCaptured.size;
  const avgRevenuePerGameMinor =
    confirmedGameCount > 0 ? Math.round(netRevenueMinor / confirmedGameCount) : null;

  // -- Venues -----------------------------------------------------------------
  const venuesTotal = apps.length;
  const venuesApproved = apps.filter((a) => a.status === "approved").length;
  // "Live" = approved AND has hosted ≥ 1 match. Need to map court→venue.
  const liveVenueIds = new Set<string>();
  for (const m of matches) {
    const vId = m.court?.venue_application_id;
    if (vId) liveVenueIds.add(vId);
  }
  const venuesLive = liveVenueIds.size;

  // Avg bookings per venue per week (last 4 weeks, across live venues)
  const fourWeeksAgo = now - 28 * dayMs;
  const recentMatches = matches.filter(
    (m) => new Date(m.created_at).getTime() >= fourWeeksAgo,
  );
  const avgBookingsPerVenuePerWeek =
    venuesLive > 0 ? recentMatches.length / venuesLive / 4 : null;

  // Bookings by day-of-week (across all confirmed matches)
  const dowCounts = new Map<string, number>();
  const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  for (const m of matches) {
    if (m.status !== "confirmed" && m.status !== "completed") continue;
    const dow = DOW[new Date(m.created_at).getDay()];
    dowCounts.set(dow, (dowCounts.get(dow) ?? 0) + 1);
  }
  // Order Mon → Sun for display
  const ORDERED_DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const bookingsByDow = ORDERED_DOW.map(
    (d) => [d, dowCounts.get(d) ?? 0] as [string, number],
  );

  // Zero-booking approved venues
  const approvedVenuesByName = new Map<string, string>();
  for (const a of apps) {
    if (a.status === "approved") {
      // We can't get the id from venue_applications directly here without
      // an extra query; rely on venue_name → check if any match's court
      // came from this venue.
      approvedVenuesByName.set(a.venue_name, a.venue_name);
    }
  }
  const liveVenueNames = new Set<string>();
  for (const m of matches) {
    const name = m.court?.venue_application?.venue_name;
    if (name) liveVenueNames.add(name);
  }
  const zeroBookingVenues: string[] = [];
  for (const name of approvedVenuesByName.keys()) {
    if (!liveVenueNames.has(name)) zeroBookingVenues.push(name);
  }

  const reviewed = apps.filter((a) => a.reviewed_at);
  const avgReviewHours =
    reviewed.length > 0
      ? Math.round(
          reviewed.reduce((sum, a) => {
            return (
              sum +
              (new Date(a.reviewed_at!).getTime() -
                new Date(a.created_at).getTime()) /
                3600_000
            );
          }, 0) / reviewed.length,
        )
      : null;
  const invitesSent = apps.filter((a) => a.invited_at).length;

  const countryCounts = new Map<string, number>();
  for (const a of apps) {
    countryCounts.set(a.country, (countryCounts.get(a.country) ?? 0) + 1);
  }
  const countrySorted = [...countryCounts.entries()].sort(
    (a, b) => b[1] - a[1],
  );
  const topCountry = countrySorted[0]?.[0] ?? null;
  const topCountryCount = countrySorted[0]?.[1] ?? null;

  // -- Retention --------------------------------------------------------------
  // 7-day: of users who signed up 14+ days ago, what % had any participant
  // row in days 7-14 after signup?
  const eligible7 = profiles.filter(
    (p) => now - new Date(p.created_at).getTime() >= 14 * dayMs,
  );
  const usersByActivityTimes = new Map<string, number[]>();
  for (const p of participants) {
    if (p.status === "left" || p.status === "released") continue;
    const arr = usersByActivityTimes.get(p.user_id) ?? [];
    arr.push(new Date(p.created_at).getTime());
    usersByActivityTimes.set(p.user_id, arr);
  }
  let retained7Count = 0;
  for (const profile of eligible7) {
    const signupT = new Date(profile.created_at).getTime();
    const acts = usersByActivityTimes.get(profile.id) ?? [];
    if (acts.some((t) => t >= signupT + 7 * dayMs && t < signupT + 14 * dayMs)) {
      retained7Count++;
    }
  }
  const retention7d =
    eligible7.length > 0
      ? Math.round((retained7Count / eligible7.length) * 100)
      : null;

  // 30-day: of users who signed up 60+ days ago, what % had activity
  // in days 30-60?
  const eligible30 = profiles.filter(
    (p) => now - new Date(p.created_at).getTime() >= 60 * dayMs,
  );
  let retained30Count = 0;
  for (const profile of eligible30) {
    const signupT = new Date(profile.created_at).getTime();
    const acts = usersByActivityTimes.get(profile.id) ?? [];
    if (
      acts.some(
        (t) => t >= signupT + 30 * dayMs && t < signupT + 60 * dayMs,
      )
    ) {
      retained30Count++;
    }
  }
  const retention30d =
    eligible30.length > 0
      ? Math.round((retained30Count / eligible30.length) * 100)
      : null;

  // Games per user per month (across users with ≥ 1 game)
  const usersWithGames = firstActivityByUser.size;
  const gamesPerUserPerMonth =
    usersWithGames > 0
      ? // Total active participations in last 30 days / users with games
        participants.filter(
          (p) =>
            (p.status === "captured" || p.status === "authorized") &&
            now - new Date(p.created_at).getTime() < 30 * dayMs,
        ).length / usersWithGames
      : null;

  // Power users - 3+ games (any status that counts as participating)
  const gamesByUser = new Map<string, number>();
  for (const p of participants) {
    if (p.status === "left" || p.status === "released") continue;
    gamesByUser.set(p.user_id, (gamesByUser.get(p.user_id) ?? 0) + 1);
  }
  let powerUserCount = 0;
  for (const v of gamesByUser.values()) if (v >= 3) powerUserCount++;
  const powerUserPct =
    usersWithGames > 0
      ? Math.round((powerUserCount / usersWithGames) * 100)
      : null;

  return {
    // Acquisition
    signupsTotal,
    signupsToday,
    signups7d,
    signups7dPrior,
    signups30d,
    signups30dPrior,
    // Activation
    activationPct,
    activatedCount,
    eligibleForActivation: eligibleForActivation.length,
    medianHoursToFirst,
    noActivationPct,
    // Games
    matchTotal,
    matchConfirmed,
    matchCancelled,
    confirmedVsCancelledRatio,
    confirmedVsCancelledRatioNumeric,
    gamesCreated7d,
    gamesCreatedAvgPerDay7d,
    avgPlayersPerGame,
    formatMix,
    topFormat,
    topFormatPct,
    cityMix,
    dailyMatchCounts,
    // Payments
    gmvMinor,
    netRevenueMinor,
    currency,
    confirmedGameCount,
    avgRevenuePerGameMinor,
    flagCount: flags.length,
    // Venues
    venuesTotal,
    venuesApproved,
    venuesLive,
    avgBookingsPerVenuePerWeek,
    bookingsByDow,
    zeroBookingVenues,
    avgReviewHours,
    invitesSent,
    topCountry,
    topCountryCount,
    // Retention
    retention7d,
    retention30d,
    gamesPerUserPerMonth,
    powerUserPct,
    powerUserCount,
    usersWithGames,
  };
}

function isSameDay(a: Date, b: Date) {
  return a.toDateString() === b.toDateString();
}
function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}
function formatHours(h: number): string {
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
}
function diffWoW(
  current: number,
  prior: number,
  label = "vs prior week",
): string | undefined {
  if (prior === 0 && current === 0) return undefined;
  if (prior === 0) return `+${current} ${label}`;
  const pct = Math.round(((current - prior) / prior) * 100);
  if (pct === 0) return `Flat ${label}`;
  const arrow = pct > 0 ? "↑" : "↓";
  return `${arrow} ${Math.abs(pct)}% ${label}`;
}

// ----------------------------------------------------------------------------
// UI primitives
// ----------------------------------------------------------------------------

function Section({
  heading,
  icon,
  badge,
  children,
}: {
  heading: string;
  icon?: React.ReactNode;
  badge?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-10">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider text-muted-foreground">
          {icon}
          {heading}
        </h2>
        {badge && (
          <span className="rounded-full bg-volt px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
            {badge}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

function Stat({
  icon,
  label,
  value,
  sub,
  tone,
  muted,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  sub?: string;
  tone?: "volt" | "danger";
  muted?: boolean;
}) {
  const valueColor =
    tone === "volt"
      ? "text-volt"
      : tone === "danger"
        ? "text-[#f87171]"
        : "";
  return (
    <div
      className={
        "rounded-xl border p-4 " +
        (muted
          ? "border-dashed border-border/60 bg-card/40"
          : "border-border bg-card")
      }
    >
      <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span className={tone === "volt" ? "text-volt" : undefined}>{icon}</span>
        {label}
      </div>
      <p className={`text-2xl font-black tracking-tight ${valueColor}`}>
        {value}
      </p>
      {sub && (
        <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
          {sub}
        </p>
      )}
    </div>
  );
}

function ComingSoon({ label, why }: { label: string; why: string }) {
  return (
    <div className="rounded-xl border border-dashed border-border/60 bg-card/30 p-4">
      <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Clock className="h-3.5 w-3.5" />
        {label}
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">{why}</p>
    </div>
  );
}

function Card({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

function BreakdownRow({
  label,
  count,
  total,
}: {
  label: string;
  count: number;
  total: number;
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <li>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="font-semibold">{label}</span>
        <span className="text-muted-foreground">
          {count} · {pct}%
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
        <div
          className="h-full rounded-full bg-volt"
          style={{ width: `${pct}%` }}
        />
      </div>
    </li>
  );
}

function DailyBars({ days }: { days: { label: string; count: number }[] }) {
  const max = Math.max(1, ...days.map((d) => d.count));
  return (
    <div className="flex h-32 items-end gap-1">
      {days.map((d, i) => {
        const h = Math.max(4, (d.count / max) * 100);
        return (
          <div
            key={i}
            className="flex flex-1 flex-col items-center justify-end gap-1"
            title={`${d.label}: ${d.count}`}
          >
            <span className="text-[9px] font-bold text-muted-foreground">
              {d.count > 0 ? d.count : ""}
            </span>
            <div
              className="w-full rounded-t-sm bg-volt/60 transition-colors hover:bg-volt"
              style={{ height: `${h}%` }}
            />
            <span className="text-[8px] text-muted-foreground">{d.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="py-6 text-center text-xs text-muted-foreground">{children}</p>
  );
}
